import { getKundli, Observer } from '@ishubhamx/panchangam-js';

/**
 * Calculate Jyotish (Vedic) astrology chart for an avatar
 * Similar to getAstrology but uses Vedic calculations
 * 
 * @param {Object} avatar - Avatar object with dob and city location
 * @param {Object} config - Optional configuration for Kundli
 * @returns {Object|null} - Object with kundli and origin, or null if data missing
 */
const toUtcDate = (value) => {
  if (!value) return null;
  let s = String(value);
  if (s.includes(' ')) s = s.replace(' ', 'T');
  if (!/T\d{2}:\d{2}(:\d{2})?/.test(s)) {
    s = s.replace(/(T\d{2}:\d{2})(?::\d{2})?/, '$1:00');
  }
  if (!/Z|[+-]\d{2}:?\d{2}$/.test(s)) s = `${s.replace(/Z$/, '')}Z`;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

export const getJyotish = (avatar, config = {}) => {
  if (!avatar?.aggr?.city?.location) {
    return null;
  }

  const birthUtc = toUtcDate(avatar.design?.meta?.personality) || toUtcDate(avatar.dob);
  if (!birthUtc) {
    console.log('Missing required birth data for Jyotish');
    return null;
  }

  try {
    const { lat, lon } = avatar.aggr.city.location;
    const elevation = avatar.aggr.city.elevation || 0;
    const birthDate = birthUtc;
    
    // Create Observer for location
    const observer = new Observer(lat, lon, elevation);
    
    // Default config
    // panchangam-js: 'whole_sign' | 'equal_house' (other values fall back to whole_sign).
    // whole_sign = standard North Indian / BPHS: entire Lagna rashi is house 1.
    // equal_house shifts cusps by Lagna degree and moves planets near sign boundaries.
    const kundliConfig = {
      houseSystem: config.houseSystem || 'whole_sign',
      ayanamsa: config.ayanamsa || 'lahiri',
      lang: config.lang || 'en',
      ...config
    };
    
    // Calculate Kundli (Vedic birth chart)
    const kundli = getKundli(birthDate, observer, kundliConfig);
    
    return {
      kundli: kundli,
      observer: observer,
      birthDate: birthDate
    };
  } catch (err) {
    console.error('Jyotish calculation failed:', err);
    throw new Error(`Jyotish calculation failed: ${err.message}`);
  }
};

/**
 * Format degrees for display in Jyotish format
 * @param {number} degree - Degree value (0-30 within a sign)
 * @returns {string} - Formatted string like "15° 30'"
 */
export const formatJyotishDegree = (degree) => {
  if (typeof degree !== 'number' || isNaN(degree)) return '';
  const degInt = Math.floor(degree);
  const minutes = Math.round((degree - degInt) * 60);
  return `${degInt}° ${String(minutes).padStart(2, '0')}'`;
};

/**
 * Get planet house number from Kundli
 * @param {Object} planet - Planet object from kundli.planets
 * @param {Array} houses - Houses array from kundli.houses
 * @returns {number|string} - House number or 'N/A'
 */
export const getPlanetHouseJyotish = (planet, houses) => {
  if (!planet?.longitude || !houses || houses.length === 0) {
    return 'N/A';
  }
  
  const planetLongitude = planet.longitude;
  
  // Find which house the planet belongs to
  for (let i = 0; i < houses.length; i++) {
    const house = houses[i];
    const nextHouse = houses[(i + 1) % houses.length];
    
    const startLongitude = house.startLongitude;
    const endLongitude = nextHouse.startLongitude;
    
    // Handle the case where we cross 0 degrees
    if (endLongitude < startLongitude) {
      // We're crossing the 0 degree point
      if (planetLongitude >= startLongitude || planetLongitude < endLongitude) {
        return house.number;
      }
    } else {
      // Normal case
      if (planetLongitude >= startLongitude && planetLongitude < endLongitude) {
        return house.number;
      }
    }
  }
  
  return 'N/A';
};

/**
 * Get Rashi (sign) name from rashi number
 * @param {number} rashi - Rashi number (0-11)
 * @returns {string} - Rashi name
 */
export const getRashiName = (rashi) => {
  const rashiNames = [
    'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
    'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
  ];
  if (typeof rashi === 'number' && rashi >= 0 && rashi < 12) {
    return rashiNames[rashi];
  }
  return 'N/A';
};
